import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import Logo from "@/components/Logo";
import { NAV, CONTACT } from "@/data/site";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled ? "glass-panel border-b border-gold/15 py-2 shadow-[0_10px_40px_-15px_rgba(0,0,0,0.6)]" : "bg-transparent py-4"
        }`}
        data-testid="site-header"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 lg:px-8">
          <Logo />
          <nav className="hidden items-center gap-1 lg:flex">
            {NAV.map((item) => {
              const active = pathname === item.to || (item.to !== "/" && pathname.startsWith(item.to));
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-link-${item.to === "/" ? "home" : item.to.slice(1).replace("/", "-")}`}
                  className={`relative rounded-full px-4 py-2 font-display text-[13px] font-semibold transition-colors duration-300 ${
                    active ? "text-gold" : "text-slate-300 hover:text-gold"
                  }`}
                >
                  {item.label}
                  {active && (
                    <motion.span layoutId="nav-pill" className="absolute inset-0 -z-10 rounded-full bg-gold/10" transition={{ type: "spring", stiffness: 350, damping: 30 }} />
                  )}
                </Link>
              );
            })}
          </nav>
          <div className="flex items-center gap-3">
            <a href={`tel:${CONTACT.phonePrimaryIntl}`} className="hidden items-center gap-2 text-sm font-semibold text-slate-300 transition-colors hover:text-gold md:flex" data-testid="header-phone-link">
              <Phone size={15} className="text-gold" />
              <span dir="ltr">{CONTACT.phonePrimary}</span>
            </a>
            <Link to="/contact" className="btn-gold hidden !px-5 !py-2.5 md:inline-flex" data-testid="header-consult-cta">مشاوره تخصصی</Link>
            <button
              onClick={() => setOpen(true)}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/25 text-gold lg:hidden"
              aria-label="باز کردن منو"
              data-testid="header-menu-open-button"
            >
              <Menu size={20} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-navy-950/95 backdrop-blur-xl lg:hidden"
            data-testid="mobile-menu-overlay"
          >
            <div className="flex items-center justify-between p-5">
              <Logo compact />
              <button
                onClick={() => setOpen(false)}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/25 text-gold"
                aria-label="بستن منو"
                data-testid="header-menu-close-button"
              >
                <X size={20} />
              </button>
            </div>
            <nav className="mt-6 flex flex-col gap-1 px-6">
              {NAV.map((item, i) => (
                <motion.div key={item.to} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}>
                  <Link
                    to={item.to}
                    className="block border-b border-gold/10 py-4 font-display text-2xl font-bold text-slate-100 transition-colors hover:text-gold"
                    data-testid={`mobile-nav-link-${item.to === "/" ? "home" : item.to.slice(1)}`}
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="mt-8">
                <Link to="/contact" className="btn-gold w-full" data-testid="mobile-menu-consult-cta">درخواست مشاوره تخصصی</Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
